import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { Icon, type IconName } from '../Icon'
import { Button } from '../control/Button'

export interface ToastSpec {
  id: number
  icon?: IconName
  message: string
  action?: { label: string; onClick: () => void }
  timeout?: number
}

interface ToastApi {
  push: (t: Omit<ToastSpec, 'id'>) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastSpec[]>([])

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback(
    (t: Omit<ToastSpec, 'id'>) => {
      const id = Date.now() + Math.floor(Math.random() * 1000)
      setItems((prev) => [...prev, { ...t, id }])
      window.setTimeout(() => dismiss(id), t.timeout ?? 5000)
    },
    [dismiss],
  )

  const api = useMemo(() => ({ push, dismiss }), [push, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-root" role="status" aria-live="polite">
          {items.map((t) => (
            <div key={t.id} className="toast">
              {t.icon && <Icon name={t.icon} />}
              <span className="grow">{t.message}</span>
              {t.action && (
                <Button variant="ghost" size="sm" onClick={t.action.onClick}>
                  {t.action.label}
                </Button>
              )}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside ToastProvider')
  return ctx
}
